import React, { memo, useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AboutUsImage from "../../../assets/images/menu-about-us-image.svg";
import HowWeWork from "../../../assets/images/menu-how-we-work-image.svg";
import {
  useAboutSectionNavActive,
  scrollToAboutSection,
} from "../../../hooks/useAboutSectionNavActive";

const DESKTOP_NAV_MIN = 961;

const AboutUsMenu = memo(({ onNavigate, mobileDrawerOpen = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const aboutSectionActive = useAboutSectionNavActive();
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);

  useEffect(() => {
    if (mobileDrawerOpen === false) setMobileAboutOpen(false);
  }, [mobileDrawerOpen]);

  const toggleAboutMobile = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof window !== "undefined" && window.innerWidth < DESKTOP_NAV_MIN) {
      setMobileAboutOpen((v) => !v);
    }
  }, []);

  const handleLink = useCallback(() => {
    onNavigate?.();
  }, [onNavigate]);

  /** Home only: always scroll to About (same-hash clicks don't re-run HomePage effect). */
  const handleAboutNavClick = useCallback(
    (e) => {
      onNavigate?.();
      if (location.pathname !== "/") return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      navigate("/#about-heading", { replace: true });
      scrollToAboutSection();
      window.setTimeout(scrollToAboutSection, 0);
      window.setTimeout(scrollToAboutSection, 120);
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToAboutSection);
      });
    },
    [location.pathname, navigate, onNavigate]
  );

  const aboutUsItems = [
    {
      id: "about",
      title: t("aboutMenu.aboutTitle"),
      description: t("aboutMenu.aboutDesc"),
      to: "/#about-heading",
      image: AboutUsImage,
    },
    {
      id: "services",
      title: t("aboutMenu.servicesTitle"),
      description: t("aboutMenu.servicesDesc"),
      to: { pathname: "/", hash: "project-assessment" },
      image: HowWeWork,
    },
  ];

  return (
    <li
      className={`full-width about-us menu-item menu-item-has-children vh-nav-about${aboutSectionActive ? " vh-nav-about--in-view" : ""}`}
    >
      <div className="nav-link vh-nav-about-bar">
        <Link
          to="/#about-heading"
          className="vh-nav-about-bar__link"
          onClick={handleAboutNavClick}
        >
          <span className="menu-title">{t("nav.aboutUs")}</span>
        </Link>
        <button
          type="button"
          className="dropdown-menu-opener vh-nav-about-chevron"
          aria-expanded={mobileAboutOpen}
          aria-label={t("nav.toggleAboutMenu")}
          onClick={toggleAboutMobile}
        />
      </div>
      <div
        className={`dropdown-menu__wrapper vh-nav-about-panel${mobileAboutOpen ? " is-open" : ""}`}
      >
        <ul className="dropdown-menu vh-nav-about-grid">
          {aboutUsItems.map(({ id, title, description, to, image }) => (
            <li key={id} className="big-size menu-item vh-nav-about-card-wrap">
              <Link
                to={to}
                className="dropdown-item vh-nav-about-card"
                onClick={handleLink}
              >
                <span className="vh-nav-about-card__kicker">
                  {t("nav.aboutExplore")}
                </span>
                <span className="menu-title vh-nav-about-card__title">
                  {title}
                </span>
                <span className="description vh-nav-about-card__desc">
                  {description}
                </span>
                <span className="image-wrap vh-nav-about-card__media">
                  <img
                    width={272}
                    height={168}
                    src={image}
                    alt=""
                    className="image"
                  />
                </span>
                <span className="vh-nav-about-card__arrow" aria-hidden="true">
                  →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
});

export default AboutUsMenu;
